import { Link } from "react-router-dom";
import { usePageMeta } from "@/hooks/usePageMeta";
import heroBg from "@/assets/hero-bg.png";

const pills = [
  { label: "Brand Strategy", to: "/services" },
  { label: "Brand Identity", to: "/services" },
  { label: "Social Media", to: "/services" },
  { label: "Brand Analysis", to: "/services" },
];

const homeCaseStudies = [
  {
    id: "02",
    caseLabel: "Case 02 · Brand Identity · Commerce",
    title: "Building Kore's Amazon storefront from zero",
    client: "Kore — D2C Shapewear Brand",
    result:
      "Full Amazon storefront built from scratch — listings, A+ content, brand page, and product imagery. Now managing ongoing.",
    stats: [
      { value: "0 → Live", label: "Storefront" },
      { value: "Ongoing", label: "Retainer" },
      { value: "A+ Content", label: "Listings" },
    ],
  },
  {
    id: "03",
    caseLabel: "Case 03 · Social Strategy · Community",
    title: "Winning IPL season without buying a single ad",
    client: "Fi Neobanking App",
    result:
      "Built relevance during the noisiest advertising moment of the year — without paid media.",
    stats: [
      { value: "200%", label: "Follower growth" },
      { value: "10,800+", label: "Hours engagement" },
      { value: "Zero", label: "Production cost" },
    ],
  },
  {
    id: "04",
    caseLabel: "Case 04 · ORM · Crisis Management",
    title: "Keeping a fintech brand credible when things broke",
    client: "Fi Money",
    result:
      "A transaction failure triggered public complaints while a campaign was live. Contained the crisis, restored trust.",
    stats: [
      { value: "0", label: "PR escalations" },
      { value: "Restored", label: "Credibility" },
      { value: "End-to-end", label: "Ownership" },
    ],
  },
];

export default function Home() {
  usePageMeta({
    title: "Work with Shivani — Brand Strategist & GTM Marketer",
    description: "Brand strategy, campaign leadership, and social media for founders, D2C brands, and agencies. Built Fi Money's brand from zero.",
    ogTitle: "Work with Shivani — Brand Strategist",
    ogDescription: "Brand strategy and campaign leadership for brands worth believing in.",
  });
  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section
        className="mx-auto max-w-[1400px] px-6 md:px-10 lg:px-14 pt-16 md:pt-24 lg:pt-28 pb-12"
        style={{
          backgroundImage: `linear-gradient(to right, rgba(0,0,0,0.6) 0%, rgba(0,0,0,0.2) 60%, rgba(0,0,0,0) 100%), url(${heroBg})`,
          backgroundSize: "cover, cover",
          backgroundPosition: "center, center",
        }}
      >
        <div className="flex items-baseline justify-between mb-10">
          <p
            className="rise text-[11px] font-sans font-medium uppercase tracking-[0.22em]"
            data-testid="hero-eyebrow"
          >
            Brand strategy <span className="opacity-50 mx-2">·</span> Campaign leadership <span className="opacity-50 mx-2">·</span> Social &amp; content
          </p>
          <p className="eyebrow hidden md:block" style={{ color: "rgba(26,26,26,0.55)" }}>
            Currently · Booking Q2 2026
          </p>
        </div>

        <div
          className="mt-10 md:mt-14 grid gap-10 md:grid-cols-[55%_40%] md:gap-12 md:items-center"
          data-testid="hero-grid"
        >
          <h1
            className="serif-display max-w-[18ch] md:max-w-none md:self-center"
            style={{ fontSize: "clamp(2.75rem, 6.2vw, 4.5rem)", lineHeight: 1.02 }}
            data-testid="hero-heading"
          >
            <span className="block rise rise-d1">
              Strategy without a campaign is just a document.
            </span>
            <span className="block rise rise-d2">
              I build both — for brands where trust isn&apos;t optional.
            </span>
          </h1>


          <div className="md:self-center md:flex md:flex-col md:justify-center">
            <p
              className="text-base leading-relaxed"
              style={{ color: "rgba(26,26,26,0.7)" }}
              data-testid="hero-paragraph"
            >
              I work with founders, D2C brands, and consumer platforms in categories where getting
              the brand wrong has real consequences. Fintech, education, homes. I write the
              positioning and ship the campaign that proves it.
            </p>
            <div className="mt-10 flex flex-wrap items-center gap-4">
              <Link
                to="/work"
                data-testid="hero-cta-see-work"
                className="inline-flex items-center gap-2 text-[0.78rem] tracking-[0.18em] uppercase font-sans px-6 py-3.5 bg-[#1A1A1A] text-[#F5F0E8] transition-opacity hover:opacity-85"
                style={{ border: "0.5px solid #1A1A1A" }}
              >
                See my work
                <span aria-hidden>→</span>
              </Link>
              <Link
                to="/contact"
                data-testid="hero-cta-work-with-me"
                className="inline-flex items-center gap-2 text-[0.78rem] tracking-[0.18em] uppercase font-sans px-6 py-3.5 transition-colors hover:bg-[#1A1A1A] hover:text-[#F5F0E8]"
                style={{ border: "0.5px solid #1A1A1A" }}
              >
                Work with me
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* DISCIPLINE MARQUEE — between hero and services */}
      <section
        className="overflow-hidden py-7 md:py-8"
        style={{
          borderTop: "0.5px solid rgba(26,26,26,0.25)",
          borderBottom: "0.5px solid rgba(26,26,26,0.25)",
        }}
        data-testid="discipline-marquee"
      >
        <div className="marquee-track">
          {[0, 1].map((dup) => (
            <div key={dup} className="flex items-center gap-12">
              {[
                "Brand Strategy",
                "Campaign Leadership",
                "Social Media",
                "Influencer Marketing",
                "Content Writing",
                "Brand Identity",
                "ORM Systems",
                "Amazon Commerce",
                "AI Visual Production",
              ].map((w, i) => (
                <span key={`${dup}-${i}`} className="flex items-center gap-12">
                  <span
                    className="font-sans text-[0.8rem] md:text-[0.85rem] tracking-[0.32em] uppercase whitespace-nowrap"
                    style={{ color: "#C9B99A" }}
                  >
                    {w}
                  </span>
                  <span style={{ color: "#C9B99A" }} className="text-base opacity-70">·</span>
                </span>
              ))}
            </div>
          ))}
        </div>
      </section>

      {/* SERVICES TEASER — What I do */}
      <section
        className="mx-auto max-w-[1400px] px-6 md:px-10 lg:px-14 py-24 md:py-32"
        data-testid="services-teaser"
      >
        <div className="grid md:grid-cols-12 gap-10 items-start">
          <div className="md:col-span-4">
            <p className="eyebrow mb-4" style={{ color: "rgba(26,26,26,0.55)" }}>
              (01) — Practice
            </p>
            <h2 className="serif-display text-5xl md:text-6xl lg:text-7xl leading-[0.95]">
              What I <span className="serif-italic">do.</span>
            </h2>
          </div>

          <div className="md:col-span-8 md:pt-6">
            <p
              className="text-base md:text-lg leading-relaxed max-w-[52ch]"
              style={{ color: "rgba(26,26,26,0.78)" }}
            >
              Four practices, one through-line — find the truest thing the brand can say, then
              build the system that helps it say it consistently, sharply, and at scale.
            </p>

            <ul className="mt-10 flex flex-wrap gap-3" data-testid="services-pills">
              {pills.map((p) => (
                <li key={p.label}>
                  <Link
                    to={p.to}
                    data-testid={`service-pill-${p.label.toLowerCase().replace(/\s+/g, "-")}`}
                    className="inline-flex items-center px-5 py-3 text-[0.82rem] tracking-[0.14em] uppercase font-sans transition-colors hover:bg-[#1A1A1A] hover:text-[#F5F0E8]"
                    style={{ border: "0.5px solid #1A1A1A" }}
                  >
                    {p.label}
                  </Link>
                </li>
              ))}
            </ul>

            <div className="mt-10">
              <Link
                to="/services"
                data-testid="services-teaser-link"
                className="font-serif text-xl md:text-2xl editorial-link"
              >
                <span className="serif-italic">See full services</span> →
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* FEATURED CASE STUDY */}
      <section
        className="mx-auto max-w-[1400px] px-6 md:px-10 lg:px-14 pb-24 md:pb-32"
        data-testid="featured-case-study"
      >
        <div className="rule mb-12" />
        <div className="grid md:grid-cols-12 gap-10 md:gap-14">
          <div className="md:col-span-4">
            <p className="eyebrow mb-4" style={{ color: "rgba(26,26,26,0.55)" }}>
              (02) — Selected work
            </p>
            <h2 className="serif-display text-4xl md:text-5xl lg:text-6xl leading-[0.98]">
              The work isn&apos;t just pretty. <span className="serif-italic">It moves people.</span>
            </h2>
          </div>

          <article className="md:col-span-8">
            <div
              style={{
                backgroundColor: "#E8E2D5",
                border: "0.5px solid rgba(26,26,26,0.5)",
              }}
            >
              <img
                src="https://customer-assets.emergentagent.com/job_shivani-studio/artifacts/xy3i7ott_sima-campaign-hero.jpg.jpg"
                alt="Fi × Sima Taparia — Mutual Fund Matchmaking campaign"
                className="block w-full h-auto"
                style={{ aspectRatio: "16 / 10", objectFit: "cover", objectPosition: "center" }}
                data-testid="home-featured-case-image"
              />
              <div className="p-8 md:p-12 lg:p-14">
              <div className="flex items-baseline justify-between gap-6 mb-8">
                <p className="eyebrow" style={{ color: "rgba(26,26,26,0.55)" }}>
                  Case 01 · Fintech · Campaign
                </p>
                <p className="eyebrow hidden sm:block" style={{ color: "rgba(26,26,26,0.55)" }}>
                  Delivered in 7 days
                </p>
              </div>

              <h3
                className="serif-display leading-[0.98] mb-8"
                style={{ fontSize: "clamp(2.25rem, 4.6vw, 3.75rem)" }}
                data-testid="featured-case-title"
              >
                Fi <span className="serif-italic font-normal" style={{ color: "rgba(26,26,26,0.55)" }}>×</span> Sima Taparia
              </h3>

              <div className="rule mb-8" style={{ opacity: 0.5 }} />

              <div className="grid sm:grid-cols-12 gap-6 mb-10">
                <p
                  className="sm:col-span-3 eyebrow"
                  style={{ color: "rgba(26,26,26,0.55)" }}
                >
                  Result
                </p>
                <p
                  className="sm:col-span-9 serif-display text-2xl md:text-3xl lg:text-[2rem] leading-[1.15]"
                  data-testid="featured-case-result"
                >
                  A campaign that made <span className="serif-italic">2M+ people</span> rethink how they invest — delivered in one week.
                </p>
              </div>

              <div className="grid sm:grid-cols-3 gap-6 pt-6" style={{ borderTop: "0.5px solid rgba(26,26,26,0.35)" }}>
                <Stat label="Video views" value="2M+" />
                <Stat label="End-to-end delivery" value="1 week" />
                <Stat label="LinkedIn engagement" value="Organic" />
              </div>

              <div className="mt-10">
                <Link
                  to="/work"
                  data-testid="featured-case-link"
                  className="font-serif text-xl md:text-2xl editorial-link"
                >
                  <span className="serif-italic">Read the case study</span> →
                </Link>
              </div>
            </div>
            </div>
          </article>

          <div
            className="md:col-span-12 grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8 mt-12 md:mt-16 items-start"
            data-testid="home-case-studies-grid"
          >
            {homeCaseStudies.map((c) => (
              <CaseStudyCard key={c.id} {...c} />
            ))}
          </div>
        </div>
      </section>

      {/* PULL QUOTE — Bani Singh */}
      <section
        className="mx-auto max-w-[1400px] px-6 md:px-10 lg:px-14 py-32 md:py-44"
        data-testid="home-pullquote"
      >
        <div className="rule mb-20 md:mb-24" />
        <div className="grid md:grid-cols-12 gap-6 md:gap-10">
          <div className="md:col-span-3">
            <p className="eyebrow" style={{ color: "rgba(26,26,26,0.55)" }}>
              Said about
            </p>
          </div>
          <div className="md:col-span-8 md:col-start-4">
            <blockquote
              className="serif-italic font-serif leading-[1.45] pl-5 md:pl-8"
              style={{
                borderLeft: "0.5px solid #1A1A1A",
                fontSize: "clamp(1.25rem, 1.6vw, 1.65rem)",
                color: "rgba(26,26,26,0.92)",
              }}
            >
              &ldquo;Shivani understood the brand instantly. She came in with a clear point of view
              and delivered visuals that looked like they were shot for Kore specifically, not
              pulled from a generic template. It was from a brand marketer&apos;s lens that&apos;s
              rare to come by.&rdquo;
            </blockquote>
            <footer className="mt-10 pl-5 md:pl-8">
              <p className="serif-display text-lg md:text-xl leading-tight">Bani Singh</p>
              <p
                className="mt-1 text-[0.85rem]"
                style={{ color: "rgba(26,26,26,0.7)" }}
              >
                Founder, Kore
              </p>
              <p
                className="text-[0.78rem] tracking-[0.04em]"
                style={{ color: "rgba(26,26,26,0.5)" }}
              >
                Forbes 30u30 Asia
              </p>
            </footer>
          </div>
        </div>
        <div className="rule mt-20 md:mt-24" />
      </section>

      {/* CLOSING CTA */}
      <section
        className="mx-auto max-w-[1400px] px-6 md:px-10 lg:px-14 pb-24 md:pb-32"
        data-testid="home-closing-cta"
      >
        <div className="rule mb-16" />
        <div className="grid md:grid-cols-12 gap-10 items-end">
          <div className="md:col-span-8">
            <p className="eyebrow mb-6" style={{ color: "rgba(26,26,26,0.55)" }}>
              (05) — Let&apos;s talk
            </p>
            <h2
              className="serif-display leading-[0.95]"
              style={{ fontSize: "clamp(2.75rem, 6vw, 5.5rem)" }}
              data-testid="closing-cta-heading"
            >
              Ready to tell your <span className="serif-italic">story right?</span>
            </h2>
            <p
              className="mt-8 text-base md:text-lg leading-relaxed max-w-[58ch]"
              style={{ color: "rgba(26,26,26,0.78)" }}
            >
              I take on a limited number of projects each quarter. If you&apos;re building
              something worth believing in, let&apos;s talk.
            </p>
          </div>
          <div className="md:col-span-4 md:text-right">
            <Link
              to="/contact"
              data-testid="closing-cta-button"
              className="inline-flex items-center gap-2 text-[0.78rem] tracking-[0.18em] uppercase font-sans px-7 py-4 bg-[#1A1A1A] text-[#F5F0E8] transition-opacity hover:opacity-85"
              style={{ border: "0.5px solid #1A1A1A" }}
            >
              Start a project
              <span aria-hidden>→</span>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}

function CaseStudyCard({ id, caseLabel, title, client, result, stats }) {
  return (
    <article data-testid={`home-case-${id}`}>
      <div
        style={{
          backgroundColor: "#E8E2D5",
          border: "0.5px solid rgba(26,26,26,0.5)",
        }}
      >
        <div className="p-8 md:p-12 lg:p-14">
          <p className="eyebrow mb-8" style={{ color: "rgba(26,26,26,0.55)" }}>
            {caseLabel}
          </p>

          <h3
            className="serif-display leading-[0.98] mb-6"
            style={{ fontSize: "clamp(1.25rem, 2vw, 1.75rem)" }}
            data-testid={`home-case-${id}-title`}
          >
            {title}
          </h3>

          <p className="eyebrow" style={{ color: "rgba(26,26,26,0.55)" }}>
            Client · {client}
          </p>

          <div className="rule my-8" style={{ opacity: 0.5 }} />

          <div className="grid sm:grid-cols-12 gap-6 mb-10">
            <p
              className="sm:col-span-3 eyebrow"
              style={{ color: "rgba(26,26,26,0.55)" }}
            >
              Result
            </p>
            <p
              className="sm:col-span-9 serif-display text-base md:text-lg leading-[1.3]"
              data-testid={`home-case-${id}-result`}
            >
              {result}
            </p>
          </div>

          <div
            className="grid sm:grid-cols-3 gap-6 pt-6"
            style={{ borderTop: "0.5px solid rgba(26,26,26,0.35)" }}
          >
            {stats.map((s) => (
              <Stat key={s.label} label={s.label} value={s.value} />
            ))}
          </div>

          <div className="mt-10">
            <Link
              to="/work"
              data-testid={`home-case-${id}-link`}
              className="font-serif text-xl md:text-2xl editorial-link"
            >
              <span className="serif-italic">Read the case study</span> →
            </Link>
          </div>
        </div>
      </div>
    </article>
  );
}

function Stat({ label, value }) {
  return (
    <div>
      <p
        className="text-[10px] font-sans font-medium uppercase tracking-[0.2em] mb-2"
        style={{ color: "rgba(26,26,26,0.55)" }}
      >
        {label}
      </p>
      <p className="serif-display text-xl md:text-2xl leading-none">{value}</p>
    </div>
  );
}
